import { supabase } from '@/lib/supabase/client';
import type { Tables } from '@/types/supabase';

export type Favorite = Tables< 'favorites' >;

const LOCAL_STORAGE_KEY = 'local_favorites';

const getLocalFavorites = () => {
	const localFavorites = localStorage.getItem( LOCAL_STORAGE_KEY );
	const result = localFavorites ? JSON.parse( localFavorites ) : [];
	// Filter out falsy values (null, undefined, empty strings, etc.)
	return result.filter( ( fav: any ) => fav );
};

export const getFavorites = async ( userId: string | null ) => {
	if ( ! userId ) {
		return getLocalFavorites();
	}

	const { data, error } = await supabase
		.from( 'favorites' )
		.select( 'estate_id' )
		.eq( 'user_id', userId );

	if ( error ) {
		return [];
	}

	return data.map( ( fav ) => fav.estate_id );
};

export const getCount = async ( userId: string | null ): Promise< number > => {
	if ( ! userId ) {
		return getLocalFavorites().length;
	}

	const { count, error } = await supabase
		.from( 'favorites' )
		.select( '*', { count: 'exact', head: true } )
		.eq( 'user_id', userId );

	if ( error ) {
		return 0;
	}

	return count ?? 0;
};

export const toggleFavorite = async (
	estateId: string,
	userId: string | null
): Promise< { isFavorite: boolean; favorites: string[] } > => {
	if ( ! userId ) {
		const localFavorites = getLocalFavorites();
		const isFavorite = localFavorites.includes( estateId );

		if ( isFavorite ) {
			const newFavorites = localFavorites.filter(
				( id: string ) => id !== estateId
			);
			localStorage.setItem(
				LOCAL_STORAGE_KEY,
				JSON.stringify( newFavorites )
			);
			return { isFavorite: false, favorites: newFavorites };
		}

		const newFavorites = [ ...localFavorites, estateId ];
		localStorage.setItem(
			LOCAL_STORAGE_KEY,
			JSON.stringify( newFavorites )
		);
		return { isFavorite: true, favorites: newFavorites };
	}

	const { data: existing } = await supabase
		.from( 'favorites' )
		.select( 'estate_id' )
		.eq( 'user_id', userId )
		.eq( 'estate_id', estateId )
		.single();

	if ( existing ) {
		await supabase
			.from( 'favorites' )
			.delete()
			.eq( 'user_id', userId )
			.eq( 'estate_id', estateId );

		// Get updated favorites list after deletion
		const updatedFavorites = await getFavorites( userId );
		return { isFavorite: false, favorites: updatedFavorites };
	}

	await supabase
		.from( 'favorites' )
		.insert( { user_id: userId, estate_id: estateId } );

	// Get updated favorites list after insertion
	const updatedFavorites = await getFavorites( userId );
	return { isFavorite: true, favorites: updatedFavorites };
};

/**
 * Idempotently add an estate to favorites (does not remove if already present).
 *
 * @param estateId Estate ID to add.
 * @param userId   Authenticated user ID, or null for localStorage favorites.
 */
export const addFavorite = async (
	estateId: string,
	userId: string | null
): Promise< { added: boolean; favorites: string[] } > => {
	if ( ! estateId ) {
		return { added: false, favorites: await getFavorites( userId ) };
	}

	if ( ! userId ) {
		const localFavorites = getLocalFavorites();
		if ( localFavorites.includes( estateId ) ) {
			return { added: false, favorites: localFavorites };
		}

		const newFavorites = [ ...localFavorites, estateId ];
		localStorage.setItem(
			LOCAL_STORAGE_KEY,
			JSON.stringify( newFavorites )
		);
		return { added: true, favorites: newFavorites };
	}

	const { data: existing } = await supabase
		.from( 'favorites' )
		.select( 'estate_id' )
		.eq( 'user_id', userId )
		.eq( 'estate_id', estateId )
		.maybeSingle();

	if ( existing ) {
		const favorites = await getFavorites( userId );
		return { added: false, favorites };
	}

	const { error } = await supabase
		.from( 'favorites' )
		.insert( { user_id: userId, estate_id: estateId } );

	if ( error ) {
		const favorites = await getFavorites( userId );
		return { added: false, favorites };
	}

	const favorites = await getFavorites( userId );
	return { added: true, favorites };
};

export const syncLocalFavorites = async ( userId: string ) => {
	const localFavorites = getLocalFavorites();

	if ( localFavorites.length === 0 ) return;

	const { error } = await supabase.from( 'favorites' ).upsert(
		localFavorites.map( ( estateId: string ) => ( {
			user_id: userId,
			estate_id: estateId,
		} ) )
	);

	if ( ! error ) {
		localStorage.removeItem( LOCAL_STORAGE_KEY );
	}
};
